import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Bell, CheckCheck } from "lucide-react";

const CHANNELS = ["in_app", "email", "sms", "push", "whatsapp"];

export default function Notifications() {
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", channel: "in_app", user_id: "" });

  const load = async () => {
    const [n, u] = await Promise.all([api.get("/notifications?limit=200"), api.get("/users")]);
    setData(n.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    try {
      const payload = { title: form.title, body: form.body, channel: form.channel };
      if (form.user_id) payload.user_id = form.user_id;
      await api.post("/notifications", payload);
      toast.success("Notification sent");
      setOpen(false);
      setForm({ title: "", body: "", channel: "in_app", user_id: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch (e) {}
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Notification Engine"
        action={
          <div className="flex gap-2">
            {data.unread_count > 0 && <Button variant="outline" onClick={markAll} className="h-9 px-4 text-[13px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="mark-all-read-button"><CheckCheck className="w-3.5 h-3.5 mr-1.5"/> Mark all read</Button>}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="send-notification-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> Send</Button></DialogTrigger>
              <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
                <DialogHeader><DialogTitle className="font-heading">Send notification</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="notification-title-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Body</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="notification-body-input"/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Channel</Label>
                      <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                        <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="notification-channel-select"><SelectValue/></SelectTrigger>
                        <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Recipient</Label>
                      <Select value={form.user_id || "broadcast"} onValueChange={(v) => setForm({ ...form, user_id: v === "broadcast" ? "" : v })}>
                        <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="notification-recipient-select"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="broadcast">All users (broadcast)</SelectItem>
                          {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="text-[10px] mono text-hocp-textTertiary">Only in_app is delivered now; email/sms/push/whatsapp channels are queued for future integration.</div>
                </div>
                <DialogFooter><Button onClick={send} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-notification-button">Send</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4 text-xs mono text-hocp-textMuted">
        <Badge tone={data.unread_count > 0 ? "warning" : "neutral"}>{data.unread_count} unread</Badge>
        <span>· {data.items.length} total in your inbox</span>
      </div>

      {data.items.length === 0 ? <EmptyState icon={Bell} title="Your inbox is empty" subtitle="Notifications targeted at you will appear here in real time."/> :
        <div className="space-y-2">
          {data.items.map((n) => (
            <Card key={n.id} className={`p-4 ${!n.is_read ? "border-l-2 border-l-blue-500" : ""}`} data-testid={`notification-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[13px] text-white">{n.title}</span>
                    <Badge tone="neutral">{n.channel}</Badge>
                    {!n.is_read && <Badge tone="info">NEW</Badge>}
                  </div>
                  <div className="text-[12px] text-hocp-textMuted mt-1.5">{n.body}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-[10px] text-hocp-textTertiary">{new Date(n.created_at).toLocaleString()}</div>
                  {!n.is_read && <Button size="sm" variant="ghost" onClick={() => markOne(n.id)} className="h-6 px-2 text-[10px] mt-1 mono uppercase" data-testid={`mark-read-${n.id}`}>Mark read</Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      }
    </div>
  );
}
